/** Small pure formatters; all take `now` so tests are exact. */

/** "0:07", "1:23", "1:02:03" */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(rest).padStart(2, "0")}`;
}

/** "just now", "3 min ago", "2 h ago", "5 days ago" */
export function formatRelative(iso: string, now: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Math.round((now - then) / 1000));
  if (diff < 60) return "just now";
  const min = Math.floor(diff / 60);
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

export function formatCredits(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return `${n.toLocaleString("en-US")} credit${n === 1 ? "" : "s"}`;
}

/** "0 B", "840 B", "12.5 KB", "25 MB", "1.2 GB" */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = Math.max(0, n);
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : value >= 100 ? 0 : 1;
  return `${value.toFixed(digits)} ${units[i]}`;
}

/** "Oct 29, 2026" — a calendar date for deadlines that are days away. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(t);
}

/** A rounded duration for estimates: "about 20 s", "about 2 min", "about 1 h 10 min". */
export function formatApprox(seconds: number): string {
  const s = Math.max(0, seconds);
  if (s < 50) return `about ${Math.max(5, Math.round(s / 5) * 5)} s`;
  if (s < 3600) return `about ${Math.max(1, Math.round(s / 60))} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m ? `about ${h} h ${m} min` : `about ${h} h`;
}

/** "18:42" in the viewer's own time zone. */
export function formatClock(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}
